import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { Boxes, FlaskConical, Lock, Play } from "lucide-react";

const NUM = [
  ["default_safety_buffer", "Default Safety Buffer (units)"],
  ["low_stock_threshold", "Low-Stock Threshold (units)"],
  ["freshness_limit_minutes", "Inventory Freshness Limit (min)"],
  ["sync_frequency_minutes", "Sync Frequency (min)"],
  ["manual_override_expiration_days", "Manual Override Expiration (days)"],
];
const BOOL = [
  ["auto_out_of_stock", "Automatic out-of-stock handling"],
  ["pause_sync", "Pause inventory sync"],
  ["simulation_mode", "Simulation mode (read-only)"],
  ["activation_approved", "Activation approved (allow live writes)"],
];

const DECISION_COLOR = {
  in_stock: "text-green-700", low_stock: "text-amber-700", out_of_stock: "text-red-600",
  below_safety_buffer_zero: "text-amber-700", needs_review_no_numeric_stock: "text-red-600",
  stale_needs_review: "text-amber-700", fetch_failed_pause: "text-red-600",
};

export default function AdminInventory() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => adminClient().get("/admin/cj/inventory-settings").then(({ data }) => setS(data)).catch(() => setS({}));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { reason: "admin inventory settings change" };
      NUM.forEach(([k]) => (payload[k] = Number(s[k]) || 0));
      BOOL.forEach(([k]) => (payload[k] = !!s[k]));
      payload.warehouse_priority = (s.warehouse_priority || ["US"]);
      const { data } = await adminClient().put("/admin/cj/inventory-settings", payload);
      setS(data); toast.success("Inventory settings saved (live sync stays disabled)");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const runTest = async () => {
    setRunning(true);
    try {
      const { data } = await adminClient().post("/admin/cj/inventory/controlled-test?limit=3");
      setTest(data); toast.success(`Read-only test done — ${data.shopify_inventory_writes} Shopify writes`);
    } catch { toast.error("Test failed"); } finally { setRunning(false); }
  };

  if (!s) return <p className="text-sm text-muted-foreground">Loading inventory settings…</p>;

  return (
    <div data-testid="admin-inventory">
      <div className="flex items-center gap-2 mb-1"><Boxes className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Live CJ Inventory</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Verified CJ warehouse stock (US-priority) minus a configurable safety buffer. <strong>Read-only in this phase</strong> — nothing is written to Shopify until you approve activation.</p>

      <div className="border border-black/10 bg-white p-5 mb-6" data-testid="inventory-settings">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Inventory Settings</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {NUM.map(([k, label]) => (
            <label key={k} className="text-[11px] font-medium text-muted-foreground">{label}
              <input type="number" value={s[k] ?? 0} onChange={(e) => set(k, e.target.value)}
                className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid={`inv-${k}`} />
            </label>
          ))}
          <label className="text-[11px] font-medium text-muted-foreground">Warehouse Priority (comma)
            <input value={(s.warehouse_priority || []).join(",")} onChange={(e) => set("warehouse_priority", e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean))}
              className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid="inv-warehouse_priority" />
          </label>
        </div>
        <div className="flex flex-wrap gap-4 mt-4">
          {BOOL.map(([k, label]) => (
            <label key={k} className="text-xs flex items-center gap-1.5 text-muted-foreground">
              <input type="checkbox" checked={!!s[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`inv-${k}`} /> {label}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={save} disabled={saving} className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="inv-save">{saving ? "Saving…" : "Save Settings"}</button>
          <span className="text-[10px] text-muted-foreground">Buffer is admin-controlled, never hard-coded. Live writes require activation approval.</span>
        </div>
      </div>

      <div className="border border-black/10 bg-white p-5" data-testid="inventory-test">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5" /> Controlled Read-Only Test (max 3–5 products)</p>
          <button onClick={runTest} disabled={running} className="bg-taven-gold text-taven-black px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:brightness-95 disabled:opacity-50" data-testid="inv-run-test"><Play className="w-3.5 h-3.5 inline mr-1" />{running ? "Running…" : "Run Test"}</button>
        </div>
        {test && (
          <div data-testid="inventory-test-result">
            <p className="text-xs mb-2">Shopify inventory writes: <strong className="text-green-700">{test.shopify_inventory_writes}</strong> · tested {test.tested_count} · {JSON.stringify(test.decision_summary)}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-taven-gray/60 text-left"><tr>
                  <th className="px-2 py-1.5">CJ PID</th><th className="px-2 py-1.5">WH</th><th className="px-2 py-1.5">CJ Qty</th>
                  <th className="px-2 py-1.5">Buffer</th><th className="px-2 py-1.5">Proposed</th><th className="px-2 py-1.5">Decision</th>
                </tr></thead>
                <tbody className="divide-y divide-black/5">
                  {test.products.map((p) => (
                    <tr key={p.cj_pid}>
                      <td className="px-2 py-1.5 font-mono">{p.cj_pid}{p.fixed_test_quantity_excluded && <span className="ml-1 text-[9px] bg-taven-gray px-1">TEST-EXCLUDED</span>}</td>
                      <td className="px-2 py-1.5">{p.country_code || "—"}</td>
                      <td className="px-2 py-1.5">{p.cj_numeric_quantity ?? "null"}</td>
                      <td className="px-2 py-1.5">{p.safety_buffer}</td>
                      <td className="px-2 py-1.5">{p.proposed_shopify_quantity ?? "—"}</td>
                      <td className={`px-2 py-1.5 font-bold ${DECISION_COLOR[p.decision] || ""}`}>{p.decision}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
